import { OddsFormat } from '@bpos';
import { MessageEnvelope } from '@cds/push';
import {
    isFixtureUpdateEnvelope,
    isGameDeleteEnvelope,
    isGameUpdateEnvelope,
    isOptionMarketDeleteEnvelope,
    isOptionMarketUpdateEnvelope,
    isParticipantUpdateEnvelope,
} from '@frontend/sports/content-distribution/feature';
import { EarlyPayoutResultedPickState } from 'packages/sports/web/app/src/my-bets-base/models/early-payout';

import { BetslipPick } from '../../core/picks/betslip-pick';
import { BetslipV1Pick } from '../../core/picks/betslip-v1-pick';
import { BetslipV2OptionMarketPick } from '../../core/picks/betslip-v2-option-market-pick';
import { BetslipV2ParticipantPick } from '../../core/picks/betslip-v2-participant-pick';
import { ResultStatus } from '../../core/picks/pick-models';
import PickPushUpdaterService from '../../integration/services/push-updaters/pick-push-updater.service';
import { IEditBetUpdatedPayload } from './actions';

export const pickUpdatedActionApplier = (action: { payload: IEditBetUpdatedPayload }, pick: BetslipPick): BetslipPick => {
    const { v1FixtureMessages, v2FixtureMessages } = action.payload;
    let messages: MessageEnvelope[] = [];
    if (BetslipV1Pick.isPick(pick)) {
        messages = Array.from(findUpdateForV1Pick(v1FixtureMessages, pick));

        if (messages.length) {
            for (const message of messages) {
                pick.updateFromMessage(message);
            }

            return pick.copy();
        }
    }

    if (BetslipV2OptionMarketPick.isPick(pick)) {
        // Usually JS 2015 allows iterating directly from Iterable but TS does not, so convert to Array.
        messages = Array.from(findUpdateForOptionPick(v2FixtureMessages, pick));
    }

    if (BetslipV2ParticipantPick.isPick(pick)) {
        messages = Array.from(findUpdateForParticipantPick(v2FixtureMessages, pick));
    }

    if (messages.length) {
        let newPick = pick;

        for (const message of messages) {
            newPick = PickPushUpdaterService.updatePickBase(pick, message, OddsFormat.None);
        }

        return newPick.copy();
    }

    return pick;
};

export const editBetUpdatedActionApplier = (action: { payload: IEditBetUpdatedPayload }, pickList: BetslipPick[]): BetslipPick[] => {
    const updatedPicks: BetslipPick[] = [];

    pickList.forEach((p) => updatedPicks.push(pickUpdatedActionApplier(action, p)));

    return updatedPicks;
};

function* findUpdateForV1Pick(envelopes: MessageEnvelope[], pick: BetslipV1Pick): Iterable<MessageEnvelope> {
    for (const envelope of envelopes) {
        if (!isFixtureUpdateEnvelope(envelope) || envelope.payload.fixtureId !== pick.id.fixtureId) {
            continue;
        }

        if (isGameDeleteEnvelope(envelope) && envelope.payload.gameId === pick.id.marketId) {
            yield envelope;
            break;
        }
        if (isGameUpdateEnvelope(envelope) && envelope.payload.game.id === pick.id.marketId) {
            yield envelope;
        }
    }
}

function* findUpdateForOptionPick(envelopes: MessageEnvelope[], pick: BetslipV2OptionMarketPick): Iterable<MessageEnvelope> {
    for (const envelope of envelopes) {
        if (!isFixtureUpdateEnvelope(envelope) || envelope.payload.fixtureId !== pick.id.fixtureId) {
            continue;
        }

        if (isOptionMarketDeleteEnvelope(envelope) && envelope.payload.marketId === pick.id.optionMarketId) {
            yield envelope;
            break;
        }

        if (isOptionMarketUpdateEnvelope(envelope) && envelope.payload.optionMarket.id === pick.id.optionMarketId) {
            yield envelope;
        }

        if (isFixtureUpdateEnvelope(envelope)) {
            yield envelope;
        }
    }
}

function* findUpdateForParticipantPick(envelopes: MessageEnvelope[], pick: BetslipV2ParticipantPick): Iterable<MessageEnvelope> {
    for (const envelope of envelopes) {
        if (!isFixtureUpdateEnvelope(envelope) || envelope.payload.fixtureId !== pick.id.fixtureId) {
            continue;
        }
        if (isParticipantUpdateEnvelope(envelope) && pick.participants.some((p) => p.fixtureParticipantId === envelope.payload.participant.id)) {
            yield envelope;
        }
    }
}

export const pickResultedStateFromEarlyPayoutResultedPickState = (state: EarlyPayoutResultedPickState): ResultStatus => {
    switch (state) {
        case EarlyPayoutResultedPickState.Won:
            return ResultStatus.Won;
        case EarlyPayoutResultedPickState.Lost:
            return ResultStatus.Lost;
        case EarlyPayoutResultedPickState.Cancelled:
            return ResultStatus.Canceled;
        default:
            throw new Error(`{state} is not known early payout resulted pick state`);
    }
};
